import { useState, useEffect, type ReactNode } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import {
  AlertCircle,
  Activity,
  BarChart3,
  ChevronLeft,
  ClipboardList,
  LayoutDashboard,
  LogOut,
  Menu,
  NotebookPen,
  TrendingUp,
  BookOpen,
  Users,
  X,
} from "lucide-react";
import { cn } from "../lib/utils";
import { api } from "../lib/api";
import { ALERTS_CHANGED_EVENT } from "../lib/alerts-events";
import { BrandLogo } from "./BrandLogo";
import { UserAvatar } from "./UserAvatar";
import type { NavAccent } from "../lib/theme";
import { NAV_ACCENTS } from "../lib/theme";

const adminNav: { to: string; label: string; icon: typeof LayoutDashboard; accent: NavAccent }[] = [
  { to: "/admin/dashboard", label: "Dashboard", icon: LayoutDashboard, accent: "blue" },
  { to: "/admin/analytics", label: "Analytics", icon: TrendingUp, accent: "teal" },
  { to: "/admin/kpis", label: "KPI Accountability", icon: BarChart3, accent: "green" },
  { to: "/admin/role-kpi-library", label: "KPI Library", icon: BookOpen, accent: "purple" },
  { to: "/admin/status", label: "Status", icon: Activity, accent: "teal" },
  { to: "/admin/tasks", label: "Tasks", icon: ClipboardList, accent: "orange" },
  { to: "/admin/work-logs", label: "Accountability Reports", icon: NotebookPen, accent: "green" },
  { to: "/admin/staff", label: "Staff & Depts", icon: Users, accent: "indigo" },
  { to: "/admin/alerts", label: "Alerts", icon: AlertCircle, accent: "red" },
];

const staffNav: { to: string; label: string; icon: typeof LayoutDashboard; accent: NavAccent }[] = [
  { to: "/staff/dashboard", label: "Dashboard", icon: LayoutDashboard, accent: "blue" },
  { to: "/staff/alerts", label: "Alerts", icon: AlertCircle, accent: "red" },
  { to: "/staff/accountability", label: "My Accountability", icon: Activity, accent: "teal" },
  { to: "/staff/kpis", label: "My KPIs", icon: BarChart3, accent: "green" },
  { to: "/staff/tasks", label: "My Tasks", icon: ClipboardList, accent: "orange" },
  { to: "/staff/work-log", label: "Daily Accountability", icon: NotebookPen, accent: "teal" },
];

interface PortalLayoutProps {
  variant: "admin" | "staff";
  userName: string;
  subtitle?: string;
  trackingPending?: boolean;
}

const SIDEBAR_STORAGE_KEY = "vpm-sidebar-collapsed";

function navItemIsActive(pathname: string, to: string): boolean {
  return pathname === to || pathname.startsWith(`${to}/`);
}

function SidebarContent({
  variant,
  userName,
  subtitle,
  nav,
  alertUnreadCount,
  collapsed = false,
  pathname,
  onNavTo,
  onNavigate,
  onLogout,
  onToggleCollapse,
}: {
  variant: "admin" | "staff";
  userName: string;
  subtitle?: string;
  nav: typeof adminNav;
  alertUnreadCount?: number;
  collapsed?: boolean;
  pathname: string;
  onNavTo: (to: string) => void;
  onNavigate?: () => void;
  onLogout: () => void;
  onToggleCollapse?: () => void;
}) {
  return (
    <>
      <div
        className={cn(
          "relative border-b border-vpm-border/60",
          collapsed ? "flex flex-col items-center px-2 py-4" : "px-5 py-4",
        )}
      >
        <div className={cn(collapsed && "w-full overflow-hidden")}>
          <BrandLogo
            size={collapsed ? "sm" : "md"}
            to={variant === "admin" ? "/admin/dashboard" : "/staff/dashboard"}
            className={collapsed ? "mx-auto max-w-[3.5rem] justify-center" : undefined}
          />
        </div>
        {onToggleCollapse && (
          <button
            type="button"
            onClick={onToggleCollapse}
            className={cn(
              "hidden rounded-lg border border-vpm-border bg-white text-vpm-forest-muted transition hover:bg-vpm-sage-light hover:text-vpm-forest lg:flex",
              collapsed ? "mt-3 p-2" : "absolute right-3 top-1/2 -translate-y-1/2 p-1.5",
            )}
            aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            <ChevronLeft className={cn("h-4 w-4 transition-transform", collapsed && "rotate-180")} />
          </button>
        )}
      </div>

      {!collapsed && (
        <p className="px-5 pt-3 text-xs font-medium text-vpm-forest-muted">
          {variant === "admin" ? "Admin Portal" : "Staff Portal"}
        </p>
      )}

      <nav className={cn("flex-1 space-y-0.5 overflow-y-auto py-5", collapsed ? "px-2" : "px-3")}>
        {!collapsed && (
          <p className="mb-3 px-3 text-[10px] font-semibold uppercase tracking-[0.15em] text-vpm-forest-muted">
            Navigation
          </p>
        )}
        {nav.map(({ to, label, icon: Icon, accent }) => {
          const accentStyles = NAV_ACCENTS[accent];
          const showAlertBadge =
            to.endsWith("/alerts") && (alertUnreadCount ?? 0) > 0;
          const isActive = navItemIsActive(pathname, to);
          return (
            <button
              key={to}
              type="button"
              title={collapsed ? label : undefined}
              aria-current={isActive ? "page" : undefined}
              onClick={() => {
                onNavTo(to);
                onNavigate?.();
              }}
              className={cn(
                "relative flex w-full items-center rounded-xl text-sm font-medium transition-all duration-200",
                collapsed ? "justify-center px-2 py-2.5" : "gap-3 px-3 py-2.5",
                isActive
                  ? cn(accentStyles.activeBg, accentStyles.activeText, "shadow-sm")
                  : "text-vpm-forest-muted hover:bg-vpm-sage-light/70 hover:text-vpm-forest",
              )}
            >
              {isActive && !collapsed && (
                <span className={cn("absolute bottom-2 left-0 top-2 w-1 rounded-r-full", accentStyles.strip)} />
              )}
              <span className="relative flex shrink-0 items-center justify-center">
                <span
                  className={cn(
                    "flex h-8 w-8 items-center justify-center rounded-lg transition-colors",
                    isActive ? accentStyles.iconBg : "bg-transparent",
                  )}
                >
                  <Icon className={cn("h-4 w-4 shrink-0", isActive ? accentStyles.icon : accentStyles.iconMuted)} />
                </span>
                {collapsed && showAlertBadge && (
                  <span className="absolute -right-0.5 -top-0.5 h-2.5 w-2.5 rounded-full bg-vpm-accent-red ring-2 ring-white" />
                )}
              </span>
              {!collapsed && (
                <>
                  <span className="flex-1 text-left">{label}</span>
                  {showAlertBadge && (
                    <span className="rounded-full bg-vpm-accent-red px-2 py-0.5 text-[10px] font-semibold text-white">
                      {alertUnreadCount! > 99 ? "99+" : alertUnreadCount}
                    </span>
                  )}
                </>
              )}
            </button>
          );
        })}
      </nav>

      <div className={cn("border-t border-vpm-border/60", collapsed ? "p-2" : "p-4")}>
        <div
          className={cn(
            "rounded-xl bg-vpm-sage-light/60",
            collapsed ? "flex justify-center p-2" : "flex items-center gap-3 p-3",
          )}
          title={collapsed ? userName : undefined}
        >
          <UserAvatar name={userName} size="md" />
          {!collapsed && (
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-semibold text-vpm-forest">{userName}</p>
              <p className="truncate text-xs text-vpm-forest-muted">{subtitle}</p>
            </div>
          )}
        </div>
        <button
          type="button"
          onClick={onLogout}
          title={collapsed ? "Sign out" : undefined}
          className={cn(
            "mt-3 flex w-full items-center rounded-full border border-vpm-border text-sm font-medium text-vpm-forest-muted transition hover:border-vpm-sage-dark hover:bg-vpm-sage-light hover:text-vpm-forest",
            collapsed ? "justify-center p-2.5" : "justify-center gap-2 px-3 py-2.5",
          )}
        >
          <LogOut className="h-4 w-4" />
          {!collapsed && "Sign out"}
        </button>
      </div>
    </>
  );
}

export function PortalLayout({
  variant,
  userName,
  subtitle,
  trackingPending,
  children,
}: PortalLayoutProps & { children: ReactNode }) {
  const navigate = useNavigate();
  const location = useLocation();

  function goToNavItem(to: string) {
    if (location.pathname === to) return;
    navigate(to);
  }
  const [mobileOpen, setMobileOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try {
      return localStorage.getItem(SIDEBAR_STORAGE_KEY) === "true";
    } catch {
      return false;
    }
  });
  const [alertUnreadCount, setAlertUnreadCount] = useState(0);
  const nav = variant === "admin" ? adminNav : staffNav;
  const currentPage =
    [...nav]
      .filter((n) => navItemIsActive(location.pathname, n.to))
      .sort((a, b) => b.to.length - a.to.length)[0]?.label ?? "Portal";

  useEffect(() => {
    const refreshCount = () => {
      const fetchCount =
        variant === "admin" ? api.admin.alertUnreadCount() : api.staff.alertUnreadCount();
      void fetchCount.then((res) => {
        if (res.success && res.data) setAlertUnreadCount((res.data as { count: number }).count);
      });
    };

    refreshCount();
    const interval = window.setInterval(refreshCount, 60_000);
    window.addEventListener(ALERTS_CHANGED_EVENT, refreshCount);
    return () => {
      window.clearInterval(interval);
      window.removeEventListener(ALERTS_CHANGED_EVENT, refreshCount);
    };
  }, [variant, location.pathname]);

  useEffect(() => {
    try {
      localStorage.setItem(SIDEBAR_STORAGE_KEY, String(sidebarCollapsed));
    } catch {
      // ignore storage errors
    }
  }, [sidebarCollapsed]);

  async function logout() {
    if (variant === "admin") await api.admin.logout();
    else await api.staff.logout();
    navigate("/");
  }

  return (
    <div className="flex min-h-screen bg-vpm-surface">
      <aside
        className={cn(
          "fixed inset-y-0 left-0 z-30 hidden flex-col border-r border-vpm-border/60 bg-white shadow-sidebar transition-[width] duration-300 ease-in-out lg:flex",
          sidebarCollapsed ? "w-[72px]" : "w-[260px]",
        )}
      >
        <SidebarContent
          variant={variant}
          userName={userName}
          subtitle={subtitle}
          nav={nav}
          alertUnreadCount={alertUnreadCount}
          collapsed={sidebarCollapsed}
          pathname={location.pathname}
          onNavTo={goToNavItem}
          onToggleCollapse={() => setSidebarCollapsed((prev) => !prev)}
          onLogout={() => void logout()}
        />
      </aside>

      <AnimatePresence>
        {mobileOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-40 bg-vpm-forest/20 backdrop-blur-sm lg:hidden"
              onClick={() => setMobileOpen(false)}
            />
            <motion.aside
              initial={{ x: -280 }}
              animate={{ x: 0 }}
              exit={{ x: -280 }}
              transition={{ type: "spring", damping: 28, stiffness: 320 }}
              className="fixed inset-y-0 left-0 z-50 flex w-[260px] flex-col border-r border-vpm-border/60 bg-white shadow-sidebar lg:hidden"
            >
              <button
                type="button"
                onClick={() => setMobileOpen(false)}
                className="absolute right-3 top-4 rounded-lg p-2 text-vpm-forest-muted hover:bg-vpm-sage-light hover:text-vpm-forest"
                aria-label="Close menu"
              >
                <X className="h-5 w-5" />
              </button>
              <SidebarContent
                variant={variant}
                userName={userName}
                subtitle={subtitle}
                nav={nav}
                alertUnreadCount={alertUnreadCount}
                pathname={location.pathname}
                onNavTo={goToNavItem}
                onNavigate={() => setMobileOpen(false)}
                onLogout={() => void logout()}
              />
            </motion.aside>
          </>
        )}
      </AnimatePresence>

      <div
        className={cn(
          "flex min-h-screen flex-1 flex-col transition-[margin] duration-300 ease-in-out",
          sidebarCollapsed ? "lg:ml-[72px]" : "lg:ml-[260px]",
        )}
      >
        <header className="sticky top-0 z-20 border-b border-vpm-border/60 bg-white/90 px-4 py-3 backdrop-blur-md sm:px-6 lg:px-8">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => setMobileOpen(true)}
                className="rounded-xl border border-vpm-border p-2 text-vpm-forest-muted transition hover:bg-vpm-sage-light lg:hidden"
                aria-label="Open menu"
              >
                <Menu className="h-5 w-5" />
              </button>
              <div>
                <p className="text-xs font-medium uppercase tracking-wider text-vpm-forest-muted">
                  {variant === "admin" ? "Administration" : "My Workspace"}
                </p>
                <p className="font-sans text-sm font-semibold text-vpm-forest sm:text-base">{currentPage}</p>
              </div>
            </div>
            <div className="hidden items-center gap-3 sm:flex">
              <span className={variant === "admin" ? "badge-info" : "badge-discovery"}>{variant === "admin" ? "Admin" : "Staff"}</span>
              <span className="text-sm text-vpm-forest-muted">{userName}</span>
            </div>
          </div>
        </header>

        <main className="flex-1 px-4 py-6 sm:px-6 sm:py-8 lg:px-8 wide:px-10 wide:py-10">
          {variant === "staff" && trackingPending && (
            <div className="page-container mb-6 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
              Accountability tracking is not enabled for your account yet. You can explore the portal; your admin will
              enable tracking when you are ready to be included in work log and KPI accountability.
            </div>
          )}
          <div className="page-container">{children}</div>
        </main>
      </div>
    </div>
  );
}
